import bankList from "../../util/bankList";
import { useEffect, useState, useRef } from "react";
import * as yup from "yup";
import axios from "axios";
import Header from "../../components/Header";
import { Formik, FieldArray } from "formik";
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Snackbar,
  Alert,
  useMediaQuery,
  CircularProgress,
  Autocomplete,
} from "@mui/material";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

// ---------- Helpers ----------
const getItemSalesPrice = (item) => Number(item?.salePrice ?? 0);

// ---------- Bank Options ----------
const bankOptions = bankList.map((b) => ({ label: b, value: b }));

export default function CreateSales() {
  const [toast, setToast] = useState({ open: false, msg: "", sev: "success" });
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const mounted = useRef(true);

  const getStoreItems = (warehouseId) =>
    api
      .get("/api/store", {
        params: { warehouseId, t: Date.now() },
      })
      .then((r) => r.data);

  const loadWarehouseItems = async (warehouseId) => {
    if (!warehouseId) {
      setItems([]);
      return;
    }

    try {
      const data = await getStoreItems(warehouseId);

      const mapped = Array.isArray(data)
        ? data.map((s) => ({
            id: s.itemId,
            name: s.Item?.name || `#${s.itemId}`,
            salePrice: s.Item?.salePrice ?? 0,
            quantity: s.quantity,
          }))
        : [];

      setItems(mapped);
    } catch {
      setToast({
        open: true,
        msg: "Failed to load warehouse items",
        sev: "error",
      });
    }
  };

  // ---------- Logged-in user info ----------
  const loggedUser = JSON.parse(localStorage.getItem("user")) || {};
  const roleName = loggedUser?.roleName || "";
  const isSalesUser = roleName.toLowerCase().includes("sales");
  const userId = loggedUser?.id || null;
  const isNonMobile = useMediaQuery("(min-width:600px)");

  // ---------- Formik ref ----------
  const formikRef = useRef();

  // ---------- Validation Schema ----------
  const SCHEMA = yup.object({
    userId: isSalesUser
      ? yup.number()
      : yup.number().required("User is required"),
    customerId: yup.string().optional(),
    warehouseId: yup.string().required("Warehouse is required"),
    credit: yup.boolean().required(),
    tinNo: yup.string().required("TIN Number is required"),
    fsNo: yup.string().required("FS Number is required"),
    machineNo: yup.string().required("Machine Number is required"),
    bank: yup.string().required("Bank selection is required"),
    salesDate: yup.date().required("Sales date is required"),
    description: yup.string().nullable(),
    paidAmount: yup
      .number()
      .typeError("Paid amount must be a number")
      .required("Paid amount is required")
      .min(0, "Paid amount must be 0 or more"),
    items: yup
      .array()
      .of(
        yup.object({
          itemId: yup.string().required("Select an item"),
          sp: yup.number().required(),
          quantity: yup
            .number()
            .required("Quantity is required")
            .min(1, "Quantity must be at least 1"),
          bonus: yup.number().nullable(),
          bonusAmount: yup.number().nullable(),
          sponsor: yup.number().nullable(),
          sponsorAmount: yup.number().nullable(),
        }),
      )
      .min(1, "Add at least one item"),
    totalPrice: yup.number().required(),
    vat: yup.number().required(),
    totalPriceFinal: yup.number().required(),
    withholdingAmount: yup.number().required(),
  });

  // ---------- Load dropdown options ----------
  const loadOptions = async () => {
    setLoading(true);
    try {
      const t = Date.now();

      // Fetch users and warehouses
      const [uRes, wRes] = await Promise.all([
        api.get("/api/user", { params: { t } }),
        api.get("/api/warehouse", { params: { t } }),
      ]);

      if (!mounted.current) return;

      const arr = (x) => (Array.isArray(x) ? x : []);
      const salesUsers = arr(uRes.data).filter(
        (u) => String(u?.Role?.name || "").toLowerCase() === "sales",
      );
      setUsers(salesUsers);
      setWarehouses(arr(wRes.data));

      // If logged-in user is a sales user, fetch their customers automatically
      if (isSalesUser && userId) {
        await loadCustomers(userId);
      }
    } catch {
      setToast({ open: true, msg: "Failed to load options", sev: "error" });
    } finally {
      if (mounted.current) setLoading(false);
    }
  };

  const loadCustomers = async (selectedUserId) => {
    if (!selectedUserId) return setCustomers([]);

    try {
      const t = Date.now();
      const res = await api.get(`/api/customer/user/${selectedUserId}`, {
        params: { t },
      });
      if (!mounted.current) return;
      setCustomers(
        Array.isArray(res.data)
          ? res.data.filter((c) => c?.type === "customer")
          : [],
      );
    } catch (err) {
      setToast({
        open: true,
        msg: err?.response?.data?.message || "Failed to load customers",
        sev: "error",
      });
      setCustomers([]);
    }
  };

  // ---------- Fetch FS and Machine ----------
  const handleFetchFsNumber = async (selectedUserId) => {
    console.log("Fetching FS for userId:", selectedUserId); // <--- debug

    try {
      const res = await api.get(`/api/machinfs/user/${selectedUserId}`, {
        params: { t: Date.now() },
        headers: { "Cache-Control": "no-cache" },
      });
      console.log("FS records response:", res.data); // <--- debug
      const fsRecords = res.data.data || [];
      if (fsRecords.length > 0 && formikRef.current) {
        const latestFS = fsRecords[fsRecords.length - 1];
        formikRef.current.setFieldValue("fsNo", latestFS.fsNo || "");
        formikRef.current.setFieldValue("machineNo", latestFS.machineNo || "");
      }
    } catch (err) {
      console.error("Failed to fetch FS records:", err);
      if (formikRef.current) {
        formikRef.current.setFieldValue("fsNo", "");
        formikRef.current.setFieldValue("machineNo", "");
      }
    }
  };

  useEffect(() => {
    mounted.current = true;
    loadOptions();
    if (isSalesUser && userId) {
      loadCustomers(userId); // sales user sees their customers automatically
      handleFetchFsNumber(userId);
    } else if (!isSalesUser && formikRef.current?.values?.userId) {
      loadCustomers(formikRef.current.values.userId);
    }
    const interval = setInterval(loadOptions, 30000);
    const onFocus = () => loadOptions();
    window.addEventListener("focus", onFocus);
    return () => {
      mounted.current = false;
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [isSalesUser, userId, formikRef.current?.values?.userId]);

  // ---------- Initial form values ----------
  const INITIALS = {
    userId: isSalesUser ? userId : "",
    customerId: "",
    warehouseId: "",
    credit: false,
    withholding: false,
    tinNo: "",
    fsNo: "",
    machineNo: "",
    bank: "",
    salesDate: new Date().toISOString().slice(0, 10),
    description: "",
    paidAmount: "",
    items: [
      {
        itemId: "",
        sp: 0,
        quantity: 1,
        bonus: 0,
        bonusAmount: 0,
        sponsor: 0,
        sponsorAmount: 0,
      },
    ],
    totalPrice: 0,
    vat: 0,
    totalPriceFinal: 0,
    withholdingAmount: 0,
  };

  // ---------- Form submission ----------
  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      const itemsToSend = values.items.map((i) => ({
        itemId: Number(i.itemId),
        quantity: Number(i.quantity),
        unitPrice: Number(i.sp),
      }));

      const totalPrice = itemsToSend.reduce(
        (sum, it) => sum + it.unitPrice * it.quantity,
        0,
      );
      const vat = +(totalPrice * 0.15).toFixed(2);
      const totalPriceFinal = +(totalPrice + vat).toFixed(2);
      let withholdingAmount = 0;

      if (totalPrice > 20000 && values.withholding) {
        withholdingAmount = +(totalPrice * 0.03).toFixed(2);
      }

      const payload = {
        userId: isSalesUser ? Number(userId) : Number(values.userId),
        warehouseId: Number(values.warehouseId),
        customerId: values.customerId ? Number(values.customerId) : null,
        totalPrice: Number(totalPrice.toFixed(2)),
        totalTaxedPrice: totalPriceFinal,
        paidAmount: Number(values.paidAmount),
        bank: values.bank || "",
        salesDate: new Date(values.salesDate).toISOString(),
        tinNo: values.tinNo,
        fsNo: values.fsNo,
        machineNo: values.machineNo,
        description: values.description || "",
        withholdingAmount,
        vat,
        items: itemsToSend,
      };

      const res = await api.post("/api/sales", payload);
      setSubmitting(false);

      if (!res || res.status >= 400) {
        const errMsg = res?.data?.message || "Submit failed";
        setToast({ open: true, msg: errMsg, sev: "error" });
        return;
      }

      setToast({ open: true, msg: "Sale Created!", sev: "success" });
      resetForm();
      await loadOptions();
    } catch (err) {
      setSubmitting(false);

      const backend = err?.response?.data;

      let errorMsg = backend?.message || "Submit failed";

      // if backend sends array errors, append them
      if (Array.isArray(backend?.errors) && backend.errors.length > 0) {
        errorMsg += ": " + backend.errors.join(", ");
      }

      setToast({
        open: true,
        msg: errorMsg,
        sev: "error",
      });
    }
  };

  return (
    <Box m="20px">
      <Header
        title="CREATE SALE"
        subtitle="Record new sale with tax, bonus, and credit"
      />
      <Formik
        innerRef={formikRef}
        initialValues={INITIALS}
        validationSchema={SCHEMA}
        onSubmit={handleSubmit}
      >
        {(bag) => {
          // ---------- Calculate totals ----------
          let totalPrice = 0;
          bag.values.items.forEach((item, idx) => {
            const sp =
              getItemSalesPrice(
                items.find((i) => String(i.id) === String(item.itemId)),
              ) || 0;
            const qty = Number(item.quantity) || 0;
            const bonus = Number(item.bonus) || 0;
            const sponsor = Number(item.sponsor) || 0;

            const bonusAmount = bonus * sp;
            const sponsorAmount = sponsor * sp;
            const total = sp * qty;

            bag.values.items[idx].sp = sp;
            bag.values.items[idx].bonusAmount = bonusAmount;
            bag.values.items[idx].sponsorAmount = sponsorAmount;

            totalPrice += total;
          });

          const vat = +(totalPrice * 0.15).toFixed(2);
          const totalPriceFinal = +(totalPrice + vat).toFixed(2);
          let withholdingAmount = 0;

          if (totalPrice > 20000 && bag.values.withholding) {
            withholdingAmount = +(totalPrice * 0.03).toFixed(2);
          }

          bag.values.totalPrice = totalPrice;
          bag.values.vat = vat;
          bag.values.totalPriceFinal = totalPriceFinal;
          bag.values.withholdingAmount = withholdingAmount;

          return (
            <form onSubmit={bag.handleSubmit}>
              {/* ---------- User / Customer / Warehouse / Bank / FS ---------- */}
              <Box
                display="grid"
                gap="20px"
                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                sx={{
                  "& > div": {
                    gridColumn: isNonMobile ? undefined : "span 4 !important",
                  },
                }}
              >
                {!isSalesUser && (
                  <TextField
                    select
                    label="User"
                    name="userId"
                    value={bag.values.userId}
                    onChange={async (e) => {
                      bag.handleChange(e);
                      const selectedUserId = e.target.value;
                      if (selectedUserId) {
                        await handleFetchFsNumber(selectedUserId); // FS number
                        await loadCustomers(selectedUserId); // fetch customers
                        bag.setFieldValue("customerId", ""); // reset customer selection
                      }
                    }}
                    onBlur={bag.handleBlur}
                    variant="filled"
                    fullWidth
                    error={bag.touched.userId && Boolean(bag.errors.userId)}
                    helperText={bag.touched.userId && bag.errors.userId}
                  >
                    {users.map((u) => (
                      <MenuItem key={u.id} value={u.id}>
                        {u.fullName || u.username}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                <Autocomplete
                  options={customers}
                  getOptionLabel={(option) =>
                    option.name || option.companyName || ""
                  }
                  value={
                    customers.find(
                      (c) => c.id === Number(bag.values.customerId),
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    bag.setFieldValue("customerId", newValue?.id || "");
                    if (newValue?.tinNumber) {
                      bag.setFieldValue("tinNo", newValue.tinNumber);
                    } else {
                      bag.setFieldValue("tinNo", "");
                    }
                  }}
                  onBlur={bag.handleBlur}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Customer"
                      variant="filled"
                      error={
                        bag.touched.customerId && Boolean(bag.errors.customerId)
                      }
                      helperText={
                        bag.touched.customerId && bag.errors.customerId
                      }
                    />
                  )}
                  noOptionsText="No customers found"
                />

                <TextField
                  select
                  label="Warehouse"
                  name="warehouseId"
                  value={bag.values.warehouseId}
                  onChange={(e) => {
                    bag.handleChange(e);
                    loadWarehouseItems(e.target.value);
                  }}
                  onBlur={bag.handleBlur}
                  variant="filled"
                  fullWidth
                  error={
                    bag.touched.warehouseId && Boolean(bag.errors.warehouseId)
                  }
                  helperText={bag.touched.warehouseId && bag.errors.warehouseId}
                >
                  {warehouses.map((w) => (
                    <MenuItem key={w.id} value={w.id}>
                      {w.name}
                    </MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Credit"
                  name="credit"
                  value={bag.values.credit ? "true" : "false"}
                  onChange={(e) =>
                    bag.setFieldValue("credit", e.target.value === "true")
                  }
                  onBlur={bag.handleBlur}
                  variant="filled"
                  fullWidth
                  error={bag.touched.credit && Boolean(bag.errors.credit)}
                  helperText={bag.touched.credit && bag.errors.credit}
                >
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>

                <TextField
                  select
                  label="Apply Withholding"
                  name="withholding"
                  value={bag.values.withholding ? "true" : "false"}
                  onChange={(e) =>
                    bag.setFieldValue("withholding", e.target.value === "true")
                  }
                  variant="filled"
                  fullWidth
                >
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </TextField>

                <TextField
                  label="TIN Number"
                  name="tinNo"
                  value={bag.values.tinNo}
                  onChange={bag.handleChange}
                  onBlur={bag.handleBlur}
                  variant="filled"
                  fullWidth
                  error={bag.touched.tinNo && Boolean(bag.errors.tinNo)}
                  helperText={bag.touched.tinNo && bag.errors.tinNo}
                />

                <TextField
                  label="FS Number"
                  name="fsNo"
                  value={bag.values.fsNo}
                  onChange={bag.handleChange}
                  onBlur={bag.handleBlur}
                  variant="filled"
                  fullWidth
                  error={bag.touched.fsNo && Boolean(bag.errors.fsNo)}
                  helperText={bag.touched.fsNo && bag.errors.fsNo}
                />

                <TextField
                  label="Machine Number"
                  name="machineNo"
                  value={bag.values.machineNo}
                  onChange={bag.handleChange}
                  onBlur={bag.handleBlur}
                  variant="filled"
                  fullWidth
                  error={bag.touched.machineNo && Boolean(bag.errors.machineNo)}
                  helperText={bag.touched.machineNo && bag.errors.machineNo}
                />

                <TextField
                  select
                  label="Bank"
                  name="bank"
                  value={bag.values.bank}
                  onChange={bag.handleChange}
                  onBlur={bag.handleBlur}
                  variant="filled"
                  fullWidth
                  error={bag.touched.bank && Boolean(bag.errors.bank)}
                  helperText={bag.touched.bank && bag.errors.bank}
                >
                  {bankOptions.map((b) => (
                    <MenuItem key={b.value} value={b.value}>
                      {b.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  type="date"
                  label="Sales Date"
                  name="salesDate"
                  value={bag.values.salesDate}
                  onChange={bag.handleChange}
                  onBlur={bag.handleBlur}
                  variant="filled"
                  fullWidth
                  error={bag.touched.salesDate && Boolean(bag.errors.salesDate)}
                  helperText={bag.touched.salesDate && bag.errors.salesDate}
                />

                <TextField
                  type="number"
                  label="Paid Amount"
                  name="paidAmount"
                  value={bag.values.paidAmount}
                  onChange={bag.handleChange}
                  onBlur={bag.handleBlur}
                  variant="filled"
                  onKeyDown={(e) => {
                    if (e.key === "-" || e.key === "e") e.preventDefault();
                  }}
                  onInput={(e) => {
                    if (+e.target.value < 0) e.target.value = 0;
                  }}
                  onWheel={(e) => e.target.blur()}
                  inputProps={{ min: 0, step: 0.01 }}
                  fullWidth
                  error={
                    bag.touched.paidAmount && Boolean(bag.errors.paidAmount)
                  }
                  helperText={bag.touched.paidAmount && bag.errors.paidAmount}
                />
                <TextField
                  label="Description (optional)"
                  name="description"
                  value={bag.values.description}
                  onChange={bag.handleChange}
                  onBlur={bag.handleBlur}
                  variant="filled"
                  fullWidth
                  multiline
                  minRows={2}
                />
              </Box>

              <FieldArray name="items">
                {({ push, remove }) => (
                  <Box sx={{ gridColumn: "span 2" }}>
                    {bag.values.items.map((item, idx) => (
                      <Box
                        key={idx}
                        display="grid"
                        gap="15px"
                        mt={2}
                        mb={1}
                        p={2}
                        borderRadius={2}
                        border="1px solid #4444"
                        gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                        sx={{
                          "& > div": {
                            gridColumn: isNonMobile
                              ? undefined
                              : "span 4 !important",
                          },
                        }}
                      >
                        <TextField
                          select
                          label="Item"
                          name={`items[${idx}].itemId`}
                          value={item.itemId}
                          onChange={bag.handleChange}
                          onBlur={bag.handleBlur}
                          variant="filled"
                          error={
                            bag.touched.items?.[idx]?.itemId &&
                            Boolean(bag.errors.items?.[idx]?.itemId)
                          }
                          helperText={
                            bag.touched.items?.[idx]?.itemId &&
                            bag.errors.items?.[idx]?.itemId
                          }
                        >
                          {items.length === 0 ? (
                            <MenuItem value="" disabled>
                              No items available for this warehouse
                            </MenuItem>
                          ) : (
                            items.map((i) => (
                              <MenuItem key={i.id} value={i.id}>
                                {i.name} — SP: {getItemSalesPrice(i)}
                              </MenuItem>
                            ))
                          )}
                        </TextField>

                        <TextField
                          label="SP"
                          value={item.sp}
                          variant="filled"
                          InputProps={{ readOnly: true }}
                        />

                        <TextField
                          type="number"
                          label="Qty"
                          name={`items[${idx}].quantity`}
                          value={item.quantity}
                          onChange={bag.handleChange}
                          onBlur={bag.handleBlur}
                          variant="filled"
                          onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "e")
                              e.preventDefault();
                          }}
                          onInput={(e) => {
                            if (+e.target.value < 0) e.target.value = 0;
                          }}
                          onWheel={(e) => e.target.blur()}
                          inputProps={{ min: 0, step: 0.01 }}
                        />

                        <TextField
                          label="Total"
                          value={(item.sp * item.quantity).toFixed(2)}
                          variant="filled"
                          InputProps={{ readOnly: true }}
                        />

                        <TextField
                          type="number"
                          label="Bonus Units"
                          name={`items[${idx}].bonus`}
                          value={item.bonus}
                          onChange={bag.handleChange}
                          onBlur={bag.handleBlur}
                          variant="filled"
                          onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "e")
                              e.preventDefault();
                          }}
                          onInput={(e) => {
                            if (+e.target.value < 0) e.target.value = 0;
                          }}
                          onWheel={(e) => e.target.blur()}
                          inputProps={{ min: 0, step: 0.01 }}
                        />

                        <TextField
                          label="Bonus Amount"
                          value={item.bonusAmount.toFixed(2)}
                          variant="filled"
                          InputProps={{ readOnly: true }}
                        />

                        <TextField
                          type="number"
                          label="Sponsor Units"
                          name={`items[${idx}].sponsor`}
                          value={item.sponsor}
                          onChange={bag.handleChange}
                          onBlur={bag.handleBlur}
                          variant="filled"
                          onKeyDown={(e) => {
                            if (e.key === "-" || e.key === "e")
                              e.preventDefault();
                          }}
                          onInput={(e) => {
                            if (+e.target.value < 0) e.target.value = 0;
                          }}
                          onWheel={(e) => e.target.blur()}
                          inputProps={{ min: 0, step: 0.01 }}
                        />

                        <TextField
                          label="Sponsor Amount"
                          value={item.sponsorAmount.toFixed(2)}
                          variant="filled"
                          InputProps={{ readOnly: true }}
                        />

                        <Button
                          type="button"
                          color="error"
                          onClick={() => remove(idx)}
                        >
                          Remove
                        </Button>
                      </Box>
                    ))}

                    <Button
                      type="button"
                      variant="outlined"
                      onClick={() =>
                        push({
                          itemId: "",
                          sp: 0,
                          quantity: 1,
                          bonus: 0,
                          bonusAmount: 0,
                          sponsor: 0,
                          sponsorAmount: 0,
                        })
                      }
                    >
                      + Add Item
                    </Button>
                  </Box>
                )}
              </FieldArray>

              <Box mt={2} display="flex" gap={2}>
                <TextField
                  label="Total Price"
                  value={bag.values.totalPrice.toFixed(2)}
                  variant="filled"
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Total VAT 15%"
                  value={bag.values.vat.toFixed(2)}
                  variant="filled"
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Total Price Final"
                  value={bag.values.totalPriceFinal.toFixed(2)}
                  variant="filled"
                  InputProps={{ readOnly: true }}
                />
                <TextField
                  label="Withholding 3%"
                  value={bag.values.withholdingAmount.toFixed(2)}
                  variant="filled"
                  InputProps={{ readOnly: true }}
                />
              </Box>

              <Box display="flex" justifyContent="end" mt={3} gap={2}>
                <Button onClick={() => bag.resetForm()} variant="outlined">
                  Reset
                </Button>
                <Box position="relative" display="inline-flex">
                  <Button
                    type="submit"
                    color="secondary"
                    variant="contained"
                    disabled={bag.isSubmitting} // disable while loading
                  >
                    Create Sale
                  </Button>

                  {bag.isSubmitting && (
                    <CircularProgress
                      size={24}
                      sx={{
                        color: "primary.main",
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        marginTop: "-12px", // half of size
                        marginLeft: "-12px",
                      }}
                    />
                  )}
                </Box>
              </Box>
            </form>
          );
        }}
      </Formik>

      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={toast.sev}
          variant="filled"
          onClose={() => setToast((s) => ({ ...s, open: false }))}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
